import { PipeTransform, Pipe } from '@angular/core';
import { VrmRegexService, VrmRegexModel } from 'src/app/services/vrm-regex.service';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

export const stringFormat = function (format, args: string[]) {
  return format.replace(/{(\d+)}/g, function (match, number) {
    return typeof args[number] != 'undefined' ? args[number] : match;
  });
};

@Pipe({
    name: 'vrmRegex',
    standalone: false
})
export class VrmRegexPipe implements PipeTransform {
  private vrmRegexes$: Observable<VrmRegexModel[]>;
  constructor(private vrmRegexService: VrmRegexService) {
    this.vrmRegexes$ = this.vrmRegexService.vrmRegexModels$;
  }

  /**
   * Transforms input npId into formatted string.
   *
   * Example usage: {{ 'DK-AA12345' | vrmRegex | async }} = 'AA 12 345'
   *
   * Result format can be overriden with format parameter.
   * You can use these format keys: {countryCode}, {numberPlate}
   * @param options Options to apply for pipe. E.g. The result format. Default value: {numberPlate}
   */
  transform(
    npId: string,
    options?: {
      defaultValue?: string;
      format?: string;
    }
  ) {
    const input = npId.toUpperCase().trim();
    const defaultOptions = {
      format: '{numberPlate}',
    };
    const mergedOptions = { ...defaultOptions, ...options };

    const countryCode = input.substring(0, 2);
    const numberPlate = input.substring(3);

    return this.vrmRegexes$.pipe(
      map((vrmRegexes) =>
        this.applyRegexAndFormatting(
          countryCode,
          numberPlate,
          vrmRegexes,
          mergedOptions.defaultValue,
          mergedOptions.format
        )
      )
    );
  }

  private applyRegexAndFormatting(
    countryCode: string,
    numberPlate: string,
    vrmRegexes: VrmRegexModel[],
    defaultValue: string,
    format: string
  ): string {
    const countryVrmRegexes = this.filterRegexesByCountry(
      countryCode,
      vrmRegexes
    );
    if (!countryVrmRegexes || countryVrmRegexes.length === 0)
      return defaultValue !== undefined
        ? defaultValue
        : this.formatInput(format, countryCode, numberPlate);

    const regexData = this.findMatchingRegexData(
      numberPlate,
      countryVrmRegexes
    );
    if (!regexData) {
      return defaultValue !== undefined
        ? defaultValue
        : this.formatInput(format, countryCode, numberPlate);
    }

    const formattedNumberplate = this.formatNumberplate(
      regexData.format,
      ...regexData.regExpMatchArray.slice(1)
    );
    return this.formatInput(format, countryCode, formattedNumberplate);
  }

  private formatInput(
    format: string,
    countryCode: string,
    numberPlate: string
  ) {
    return format
      .replace('{countryCode}', countryCode)
      .replace('{numberPlate}', numberPlate);
  }

  private findMatchingRegexData(
    numberPlate: string,
    countryVrmRegexes: VrmRegexModel[]
  ): { format: string; regExpMatchArray: RegExpMatchArray } | null {
    let regexData: { format: string; regExpMatchArray: RegExpMatchArray };
    for (let i = 0; i < countryVrmRegexes.length; i++) {
      const regex = this.createRegex(countryVrmRegexes[i].regex);
      const regExpMatchArray = numberPlate.match(regex);
      if (regExpMatchArray) {
        regexData = { format: countryVrmRegexes[i].format, regExpMatchArray };
        break;
      }
    }
    if (!regexData) {
      return null;
    }

    return regexData;
  }

  private formatNumberplate(format: string, ...args: string[]) {
    return stringFormat(format, args);
  }

  private filterRegexesByCountry(
    countryCode: string,
    vrmRegexes: VrmRegexModel[]
  ) {
    return vrmRegexes.filter(
      (vrmRegex) => vrmRegex.countryCode === countryCode
    );
  }

  private createRegex(inputRegex: string): RegExp {
    let regexString: string = inputRegex;

    if (!inputRegex.startsWith('^')) regexString = `^${regexString}`;

    if (!inputRegex.endsWith('$')) regexString = `${regexString}$`;

    // temporary fix - replace (PREFIX) with [A-Z]{1,2,3}
    if (inputRegex.includes('(PREFIX)')) {
      regexString = regexString.replace('(PREFIX)', '([A-ZÄÖÜ]{1,3})-');
    }

    return new RegExp(regexString, 'm');
  }
}
